import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Form,
  FormGroup,
  FormHelperText,
  FormSelect,
  FormSelectOption,
  HelperText,
  HelperTextItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Switch,
  TextInput,
} from '@patternfly/react-core';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useImpersonation } from '../../../contexts/impersonation/ImpersonationContext';
import useClients from '../../../hooks/useClients';
import {
  type AdminCertificatePlatform,
  type AdminCertificatePlatformCreateMetadata,
  type AdminCertificatePlatformCreateRequest,
  type AdminCertificatePlatformType,
  type AdminCertificatePlatformUpdateMetadata,
  type AdminCertificatePlatformUpdateRequest,
  CERTIFICATE_PLATFORM_TYPES,
  CERTIFICATE_PLATFORM_TYPE_LABELS,
  ZEROSSL_DEFAULT_BASE_URL,
} from '../../../types/models/AdminCertificatePlatform';
import type { ApiResponseAdminCertificatePlatformSingle } from '../../../types/api/ApiResponseAdminCertificatePlatform';
import ClientTypeahead, { type ClientOption } from '../ProductsAndPrices/ClientTypeahead';

// Backend field-error keys this form renders inline, on the matching input. Metadata sub-field
// errors arrive under DOTTED keys (e.g. data["metadata.base_url"]). Anything else in the `data`
// map (metadata, body, platform, id, …) is surfaced in the form-level alert instead.
const FIELD_ERROR_KEYS = new Set([
  'name',
  'type',
  'clientID',
  'metadata.base_url',
  'metadata.api_key',
  'metadata.api_secret',
  'metadata.verify_tls',
  'metadata.ca_uuid',
]);

interface CertificatePlatformModalProps {
  /** Row to edit, or null to create a new platform. */
  platform: AdminCertificatePlatform | null;
  onClose: () => void;
  /** Upsert the created/updated row into the parent's list (no full refetch). */
  onSaved: (platform: AdminCertificatePlatform) => void;
}

/**
 * Create/edit modal for an admin certificate platform. The backend's `metadata` JSON is exploded
 * into individual form fields — the user never sees raw JSON.
 *
 * Secrets are write-only: the API key/secret inputs start empty in edit mode ("leave blank to
 * keep"), are only included in the PATCH body when the user typed a new value, and nothing that
 * looks like a stored value is ever displayed. One backend rule is mirrored client-side: changing
 * the endpoint URL requires re-entering the type's full credential set in the same request
 * (OPNsense: api_key + api_secret; zerossl: api_key alone) — anti-exfiltration: stored credentials
 * are never sent to an endpoint they were not explicitly given for.
 *
 * The parent mounts this component only while the modal is open, so every open starts from a
 * fresh mount: state initializers seed straight from `platform`, and the client-list fetch below
 * only runs when the modal is actually used.
 */
function CertificatePlatformModal({ platform, onClose, onSaved }: CertificatePlatformModalProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  // GET /clients, NOT useImpersonationTargets: available-targets excludes the
  // caller's own client, so it could never dedicate a platform to the
  // Ancalagon house client — which zerossl (always-dedicated) makes a hard
  // requirement, and which is legitimate for any type's infrastructure.
  const { clients, loading: clientsLoading, error: clientsError } = useClients();
  // /clients is rejected during an impersonation session (backend rule) —
  // used only to explain that failure mode instead of a generic error.
  const { isImpersonating } = useImpersonation();

  const isEdit = platform !== null;

  const [name, setName] = useState(platform?.name ?? '');
  const [type, setType] = useState<AdminCertificatePlatformType>(platform?.type ?? 'opnsense-api-trust');
  const [isShared, setIsShared] = useState(platform ? platform.clientID === null : true);
  const [clientId, setClientId] = useState<number | null>(platform?.clientID ?? null);
  const [baseUrl, setBaseUrl] = useState(platform?.metadata.base_url ?? '');
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [verifyTls, setVerifyTls] = useState(platform?.metadata.verify_tls ?? true);
  const [caUuid, setCaUuid] = useState(platform?.metadata.ca_uuid ?? '');

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const clientOptions = useMemo<ClientOption[]>(() => clients.map((c) => ({ id: c.id, name: c.name })), [clients]);

  const isTrust = type === 'opnsense-api-trust';
  const isZerossl = type === 'zerossl';
  // ZeroSSL platforms are ALWAYS dedicated (the customer brings their own API key): the backend
  // requires a clientID at create and rejects re-scoping to shared. The Switch is forced off and
  // disabled for them, and this derived value keeps the body/validation logic honest even if the
  // shared flag was toggled before the type was switched to zerossl.
  const effectiveShared = isZerossl ? false : isShared;
  // Re-sending an identical base_url does not trigger the backend's anti-exfiltration rule, so
  // only an actual change forces the credentials. On zerossl rows an EMPTIED field means "keep the
  // stored endpoint" (there is no contract way to revert to the driver default), so only a
  // non-empty differing value counts as a change there.
  const baseUrlChanged =
    platform !== null &&
    baseUrl.trim() !== (platform.metadata.base_url ?? '') &&
    (!isZerossl || baseUrl.trim() !== '');
  // The anti-exfiltration rule requires the TYPE's full credential set: api_key + api_secret for
  // OPNsense, api_key alone for zerossl (which has no secret).
  const credentialsProvided = apiKey.trim() !== '' && (isZerossl || apiSecret.trim() !== '');

  // The CA UUID is required in practice for the Trust driver: calls to the platform FAIL without
  // it. The backend still accepts an absent ca_uuid (legacy hand-seeded rows exist), so on edit an
  // already-empty value may stay empty — renaming/re-scoping a legacy row must keep working — but
  // a stored UUID may never be cleared, and create always requires one.
  const originalCaUuid = platform?.metadata.ca_uuid ?? '';
  const caUuidCleared = isEdit && isTrust && originalCaUuid !== '' && caUuid.trim() === '';
  const caUuidValid = !isTrust || (isEdit ? !caUuidCleared : caUuid.trim() !== '');

  const buildCreateBody = (): AdminCertificatePlatformCreateRequest => {
    // The metadata key set is type-specific and unknown keys are REJECTED: zerossl takes api_key
    // (+ optional base_url / verify_tls) and refuses api_secret / ca_uuid outright.
    const metadata: AdminCertificatePlatformCreateMetadata = {
      api_key: apiKey.trim(),
      verify_tls: verifyTls,
    };
    if (!isZerossl) {
      metadata.base_url = baseUrl.trim();
      metadata.api_secret = apiSecret.trim();
    } else if (baseUrl.trim() !== '') {
      // Omitted = the driver default (ZEROSSL_DEFAULT_BASE_URL).
      metadata.base_url = baseUrl.trim();
    }
    if (isTrust && caUuid.trim() !== '') {
      metadata.ca_uuid = caUuid.trim();
    }
    const body: AdminCertificatePlatformCreateRequest = { name: name.trim(), type, metadata };
    if (!effectiveShared && clientId !== null) {
      body.clientID = clientId;
    }
    return body;
  };

  // PATCH is partial and metadata is merged server-side: send only what actually changed, never
  // `type` (immutable), credentials only when the user typed a new value, and `ca_uuid: null` to
  // clear a stored UUID when the field was emptied.
  const buildUpdateBody = (original: AdminCertificatePlatform): AdminCertificatePlatformUpdateRequest => {
    const body: AdminCertificatePlatformUpdateRequest = {};
    if (name.trim() !== original.name) {
      body.name = name.trim();
    }
    const nextClientID = effectiveShared ? null : clientId;
    if (nextClientID !== original.clientID) {
      body.clientID = nextClientID;
    }
    const metadata: AdminCertificatePlatformUpdateMetadata = {};
    if (baseUrlChanged) {
      metadata.base_url = baseUrl.trim();
    }
    if (apiKey.trim() !== '') {
      metadata.api_key = apiKey.trim();
    }
    // zerossl rows have no secret — the backend rejects the key even in a PATCH.
    if (!isZerossl && apiSecret.trim() !== '') {
      metadata.api_secret = apiSecret.trim();
    }
    if (verifyTls !== original.metadata.verify_tls) {
      metadata.verify_tls = verifyTls;
    }
    if (isTrust) {
      const nextCaUuid = caUuid.trim();
      // Never send `ca_uuid: null`: clearing a stored UUID is blocked in the UI (the Trust
      // driver's calls fail without one), and a legacy-empty value is simply left untouched.
      if (nextCaUuid !== '' && nextCaUuid !== (original.metadata.ca_uuid ?? '')) {
        metadata.ca_uuid = nextCaUuid;
      }
    }
    if (Object.keys(metadata).length > 0) {
      body.metadata = metadata;
    }
    return body;
  };

  // An empty PATCH change-set is rejected by the backend ("nothing to update"), so Save stays
  // disabled until something differs from the loaded row.
  const hasChanges = platform === null || Object.keys(buildUpdateBody(platform)).length > 0;

  const canSubmit =
    !isSaving &&
    name.trim() !== '' &&
    (effectiveShared || clientId !== null) &&
    caUuidValid &&
    (isEdit
      ? hasChanges && (!baseUrlChanged || (baseUrl.trim() !== '' && credentialsProvided))
      : // zerossl needs no endpoint at create (driver default applies), just the API key.
        (isZerossl || baseUrl.trim() !== '') && credentialsProvided);

  const clearFieldError = (key: string) => {
    setFieldErrors((prev) => {
      if (!(key in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const splitFieldErrors = (
    body: ApiResponseAdminCertificatePlatformSingle | null,
  ): { errors: Record<string, string>; general: string | null } => {
    const errors: Record<string, string> = {};
    const generalParts: string[] = [];
    if (body?.data) {
      for (const [key, value] of Object.entries(body.data)) {
        if (typeof value !== 'string') {
          continue; // skips the certificate_platform object on the success shape
        }
        if (FIELD_ERROR_KEYS.has(key)) {
          errors[key] = value;
        } else {
          generalParts.push(value);
        }
      }
    }
    let general = generalParts.length > 0 ? generalParts.join(' ') : null;
    if (general === null && Object.keys(errors).length === 0) {
      general = body?.message ?? 'Could not save the certificate platform. Please try again.';
    }
    return { errors, general };
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    setFieldErrors({});
    setFormError(null);
    try {
      const response = await apiFetch(
        platform !== null ? `/admin/certificate-platforms/${platform.id}` : '/admin/certificate-platforms',
        {
          method: platform !== null ? 'PATCH' : 'POST',
          body: JSON.stringify(platform !== null ? buildUpdateBody(platform) : buildCreateBody()),
        },
      );
      // Branch on the JSend `body.status`, NEVER `response.ok`: the backend maps controller
      // exceptions to `status: 'error'` at HTTP 200, so `response.ok` can be true on a failure.
      const body = (await response.json().catch(() => null)) as ApiResponseAdminCertificatePlatformSingle | null;

      if (body?.status === 'success' && body.data?.certificate_platform) {
        const saved = body.data.certificate_platform;
        onSaved(saved);
        addNotification({
          title: isEdit ? 'Certificate platform updated' : 'Certificate platform created',
          srTitle: 'Success alert',
          description: `${saved.name} was ${isEdit ? 'updated' : 'created'}.`,
          variant: 'success',
        });
        setDrawerOpen(true);
        onClose();
        return;
      }

      const { errors, general } = splitFieldErrors(body);
      setFieldErrors(errors);
      setFormError(general);
      addNotification({
        title: isEdit ? 'Could not update certificate platform' : 'Could not create certificate platform',
        srTitle: 'Error alert',
        description: general ?? Object.values(errors)[0] ?? 'Please review the highlighted fields.',
        variant: 'danger',
      });
      setDrawerOpen(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
      setFormError(message);
      addNotification({
        title: isEdit ? 'Could not update certificate platform' : 'Could not create certificate platform',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setIsSaving(false);
    }
  };

  const errorHelper = (key: string) =>
    fieldErrors[key] !== undefined && (
      <FormHelperText>
        <HelperText>
          <HelperTextItem variant="error">{fieldErrors[key]}</HelperTextItem>
        </HelperText>
      </FormHelperText>
    );

  const defaultHelper = (text: string) => (
    <FormHelperText>
      <HelperText>
        <HelperTextItem>{text}</HelperTextItem>
      </HelperText>
    </FormHelperText>
  );

  const credentialsHelper = (key: 'metadata.api_key' | 'metadata.api_secret') => {
    const inline = errorHelper(key);
    if (inline) {
      return inline;
    }
    if (!isEdit) {
      return isZerossl
        ? defaultHelper('The client’s own ZeroSSL account API key — stored write-only, never displayed again.')
        : null;
    }
    const credentialNoun = isZerossl ? 'API key' : 'credentials';
    return defaultHelper(
      baseUrlChanged
        ? `Changing the endpoint requires re-entering the ${credentialNoun}.`
        : `Leave blank to keep the current ${credentialNoun}.`,
    );
  };

  return (
    <Modal variant="medium" isOpen onClose={onClose}>
      <ModalHeader title={isEdit ? 'Edit certificate platform' : 'Add certificate platform'} />
      <ModalBody>
        <Form
          id="certificate-platform-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (canSubmit) {
              void handleSubmit();
            }
          }}
        >
          {formError !== null && (
            <Alert
              variant="danger"
              isInline
              title={isEdit ? 'Update failed' : 'Creation failed'}
              className="pf-v6-u-mb-md"
            >
              {formError}
            </Alert>
          )}
          {isEdit && platform !== null && !platform.has_api_credentials && (
            <Alert variant="warning" isInline title="No stored API credentials" className="pf-v6-u-mb-md">
              {isZerossl
                ? 'This platform has no API key on record and cannot operate until one is entered below.'
                : 'This platform has no API key/secret on record and cannot sync until they are entered below.'}
            </Alert>
          )}

          <FormGroup label="Name" isRequired fieldId="platform-name">
            <TextInput
              id="platform-name"
              type="text"
              isRequired
              value={name}
              onChange={(_event, value) => {
                setName(value);
                clearFieldError('name');
              }}
              validated={fieldErrors['name'] !== undefined ? 'error' : 'default'}
              isDisabled={isSaving}
            />
            {errorHelper('name')}
          </FormGroup>

          <FormGroup label="Type" isRequired fieldId="platform-type">
            <FormSelect
              id="platform-type"
              value={type}
              onChange={(_event, value) => {
                setType(value as AdminCertificatePlatformType);
                clearFieldError('type');
              }}
              aria-label="Platform type"
              isDisabled={isSaving || isEdit}
            >
              {CERTIFICATE_PLATFORM_TYPES.map((t) => (
                <FormSelectOption key={t} value={t} label={CERTIFICATE_PLATFORM_TYPE_LABELS[t]} />
              ))}
            </FormSelect>
            {errorHelper('type') ||
              (isEdit && defaultHelper('Type is immutable — create a new platform for a different driver.'))}
          </FormGroup>

          <FormGroup label="Scope" fieldId="platform-scope-shared">
            <Switch
              id="platform-scope-shared"
              label="Shared platform (visible to every tenant)"
              isChecked={effectiveShared}
              onChange={(_event, checked) => {
                setIsShared(checked);
                clearFieldError('clientID');
              }}
              // ZeroSSL platforms are always dedicated — the backend requires a clientID at create
              // and rejects re-scoping to shared (the customer's API key must never serve others).
              isDisabled={isSaving || isZerossl}
            />
            {!effectiveShared && (
              <div className="pf-v6-u-mt-sm">
                <ClientTypeahead
                  clients={clientOptions}
                  value={clientId}
                  onChange={(id) => {
                    setClientId(id);
                    clearFieldError('clientID');
                  }}
                  isDisabled={isSaving || clientsLoading}
                />
              </div>
            )}
            {errorHelper('clientID') ||
              (!effectiveShared && clientsError !== null && (
                <FormHelperText>
                  <HelperText>
                    <HelperTextItem variant="warning">
                      {isImpersonating
                        ? 'The client list is unavailable during an impersonation session — end the impersonation and reopen the modal.'
                        : 'Could not load the client list — try reopening the modal.'}
                    </HelperTextItem>
                  </HelperText>
                </FormHelperText>
              )) ||
              (isZerossl &&
                defaultHelper(
                  'ZeroSSL platforms are always dedicated to one client — the customer brings their own API key.',
                )) ||
              (!effectiveShared && defaultHelper('Dedicated platforms are visible only to the selected client.'))}
          </FormGroup>

          <FormGroup label="Endpoint URL" isRequired={!isEdit && !isZerossl} fieldId="platform-base-url">
            <TextInput
              id="platform-base-url"
              type="url"
              isRequired={!isEdit && !isZerossl}
              placeholder={isZerossl ? ZEROSSL_DEFAULT_BASE_URL : 'https://firewall.example.com:8443'}
              value={baseUrl}
              onChange={(_event, value) => {
                setBaseUrl(value);
                clearFieldError('metadata.base_url');
              }}
              validated={fieldErrors['metadata.base_url'] !== undefined ? 'error' : 'default'}
              isDisabled={isSaving}
            />
            {errorHelper('metadata.base_url') ||
              defaultHelper(
                isZerossl
                  ? // Once a custom endpoint is stored there is no contract way back to the driver
                    // default (only verify_tls/ca_uuid support explicit-null removal), so an
                    // emptied field on edit means "keep" — the helper must not promise otherwise.
                    isEdit && (platform?.metadata.base_url ?? null) !== null
                    ? 'Root URL of the ZeroSSL API. Leave blank to keep the current endpoint.'
                    : `Root URL of the ZeroSSL API. Leave blank to use the default (${ZEROSSL_DEFAULT_BASE_URL}).`
                  : 'Root URL of the upstream OPNsense API endpoint.',
              )}
          </FormGroup>

          <FormGroup label="API key" isRequired={!isEdit || baseUrlChanged} fieldId="platform-api-key">
            <TextInput
              id="platform-api-key"
              type="password"
              autoComplete="new-password"
              isRequired={!isEdit || baseUrlChanged}
              value={apiKey}
              onChange={(_event, value) => {
                setApiKey(value);
                clearFieldError('metadata.api_key');
              }}
              validated={fieldErrors['metadata.api_key'] !== undefined ? 'error' : 'default'}
              isDisabled={isSaving}
            />
            {credentialsHelper('metadata.api_key')}
          </FormGroup>

          {/* ZeroSSL authenticates with a single API key — the backend rejects `api_secret` for
              that type as an unknown metadata key, so the field disappears entirely. */}
          {!isZerossl && (
            <FormGroup label="API secret" isRequired={!isEdit || baseUrlChanged} fieldId="platform-api-secret">
              <TextInput
                id="platform-api-secret"
                type="password"
                autoComplete="new-password"
                isRequired={!isEdit || baseUrlChanged}
                value={apiSecret}
                onChange={(_event, value) => {
                  setApiSecret(value);
                  clearFieldError('metadata.api_secret');
                }}
                validated={fieldErrors['metadata.api_secret'] !== undefined ? 'error' : 'default'}
                isDisabled={isSaving}
              />
              {credentialsHelper('metadata.api_secret')}
            </FormGroup>
          )}

          <FormGroup label="TLS verification" fieldId="platform-verify-tls">
            <Switch
              id="platform-verify-tls"
              label="Verify the upstream TLS certificate"
              isChecked={verifyTls}
              onChange={(_event, checked) => {
                setVerifyTls(checked);
                clearFieldError('metadata.verify_tls');
              }}
              isDisabled={isSaving}
            />
            {errorHelper('metadata.verify_tls')}
          </FormGroup>

          {/* Only the Trust driver reads ca_uuid; the ACME driver ignores it, so the field is
              hidden for ACME platforms (and never sent for them). */}
          {isTrust && (
            <FormGroup label="CA UUID" isRequired fieldId="platform-ca-uuid">
              <TextInput
                id="platform-ca-uuid"
                type="text"
                isRequired
                placeholder="15c9da2b-20e6-419f-ae60-e13bf4918e53"
                value={caUuid}
                onChange={(_event, value) => {
                  setCaUuid(value);
                  clearFieldError('metadata.ca_uuid');
                }}
                validated={fieldErrors['metadata.ca_uuid'] !== undefined || caUuidCleared ? 'error' : 'default'}
                isDisabled={isSaving}
              />
              {errorHelper('metadata.ca_uuid') ||
                (caUuidCleared && (
                  <FormHelperText>
                    <HelperText>
                      <HelperTextItem variant="error">
                        The issuing-CA UUID cannot be cleared — calls to the platform fail without it.
                      </HelperTextItem>
                    </HelperText>
                  </FormHelperText>
                )) ||
                (isEdit && originalCaUuid === '' && (
                  <FormHelperText>
                    <HelperText>
                      <HelperTextItem variant="warning">
                        No issuing-CA UUID is configured — calls to this platform will fail until one is set.
                      </HelperTextItem>
                    </HelperText>
                  </FormHelperText>
                )) ||
                defaultHelper('UUID of the issuing CA on the upstream trust store — calls to the platform fail without it.')}
            </FormGroup>
          )}
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="primary"
          type="submit"
          form="certificate-platform-form"
          isDisabled={!canSubmit}
          isLoading={isSaving}
        >
          {isEdit ? 'Save' : 'Create'}
        </Button>
        <Button variant="link" onClick={onClose} isDisabled={isSaving}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default CertificatePlatformModal;
